/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { classNames } from '../utils/UNSAFE_classNames';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { LinkVariantOptions } from './themes/LinkStyles.css';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { LinkRedwoodTheme } from './themes/redwood/LinkTheme';

type Props = {
  /**
   * Sets the URL that the hyperlink points to.
   */
  href: string;

  /**
   * Sets the variant. The default value of this property is theme driven.
   */
  variant?: 'standard' | 'primary' | 'secondary';

  /**
   * Sets the placement.
   */
  placement?: 'standalone' | 'embedded';

  /**
   * Sets the target.
   */
  target?: string;

  /**
   * Specifies id for element (or elements) that describes the object.
   */
  'aria-describedby'?: string;

  /**
   * A label to be used for accessibility purposes.
   */
  'aria-label'?: string;

  /**
   * One or more ids (separated by spaces) of elements that label the Link.
   */
  'aria-labelledby'?: string;

  /**
   * Triggered when a link is clicked, whether by keyboard, mouse, or touch events.
   */
  onClick?: (event: Event) => void;

  /**
   * Specifies the children
   */
  children?: ComponentChildren;
};

const noop = () => {};

export function Link({
  href,
  variant,
  placement,
  target,
  'aria-describedby': ariaDescribedBy,
  'aria-label': accessibleLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  onClick
}: Props) {
  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle } = useInteractionStyle();
  const { classes } = useComponentTheme<LinkVariantOptions>(LinkRedwoodTheme, {
    needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
    pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
    variant,
    placement
  });
  const linkClasses = classNames([classes]);
  const { pressProps } = usePress(onClick ?? noop);
  return (
    <a
      href={href}
      aria-describedby={ariaDescribedBy}
      aria-label={accessibleLabel}
      aria-labelledby={ariaLabelledBy}
      target={target}
      {...pressProps}
      {...interactionProps}
      class={linkClasses}>
      {children}
    </a>
  );
}
